import { useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { motion } from "framer-motion";
import { Lightbulb, Printer, Gauge, Camera, Sun, Lightning, Sparkle, ListChecks } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { jsPDF } from "jspdf";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

// ---------------------------------------------------------------------------
// Visual: side-by-side diagram showing incident vs reflected metering. SVG
// stick-figures, no external images required.
// ---------------------------------------------------------------------------
function IncidentVsReflectedDiagram({ lang }) {
  const incident = lang === "es" ? "Incidente (mide luz hacia el sujeto)" : "Incident (measures light falling on subject)";
  const reflected = lang === "es" ? "Reflejada (mide luz que rebota del sujeto)" : "Reflected (measures light bouncing off subject)";
  const subjectLabel = lang === "es" ? "Sujeto" : "Subject";
  const meterLabel = lang === "es" ? "Fotometro" : "Meter";
  const cameraLabel = lang === "es" ? "Camara" : "Camera";

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {/* INCIDENT */}
      <div className="bg-[#1a1a1d] border border-white/10 rounded-lg p-4">
        <p className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-3 text-center">{incident}</p>
        <svg viewBox="0 0 220 150" className="w-full h-32" aria-hidden="true">
          {/* Sun / light source upper-left */}
          <circle cx="30" cy="30" r="14" fill="#F5A524" />
          {[...Array(8)].map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <line key={i} x1={30 + Math.cos(a) * 18} y1={30 + Math.sin(a) * 18} x2={30 + Math.cos(a) * 24} y2={30 + Math.sin(a) * 24} stroke="#F5A524" strokeWidth="1.5" />;
          })}
          {/* Light rays toward subject */}
          {[35, 50, 65].map((y, i) => (
            <line key={i} x1="48" y1={y} x2="105" y2="85" stroke="#F5A524" strokeWidth="0.8" strokeDasharray="2,2" opacity="0.6" />
          ))}
          {/* Subject (stick figure) at center */}
          <circle cx="120" cy="70" r="8" fill="none" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="78" x2="120" y2="105" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="88" x2="108" y2="100" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="88" x2="132" y2="100" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="105" x2="112" y2="125" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="105" x2="128" y2="125" stroke="#D4D4D8" strokeWidth="2" />
          {/* Meter (white dome) at subject — pointing back at camera */}
          <circle cx="135" cy="90" r="6" fill="#F5A524" stroke="#fff" strokeWidth="1.5" />
          {/* Camera on right */}
          <rect x="180" y="68" width="22" height="14" rx="2" fill="#52525B" stroke="#A1A1AA" />
          <circle cx="191" cy="75" r="4" fill="#27272A" stroke="#A1A1AA" />
          {/* Arrow from meter dome to camera */}
          <line x1="141" y1="88" x2="178" y2="78" stroke="#10B981" strokeWidth="1.5" markerEnd="url(#arrow)" />
          <defs>
            <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <polygon points="0 0, 6 3, 0 6" fill="#10B981" />
            </marker>
          </defs>
          {/* Labels */}
          <text x="120" y="142" textAnchor="middle" fill="#71717A" fontSize="9" fontFamily="monospace">{subjectLabel} + {meterLabel}</text>
          <text x="191" y="98" textAnchor="middle" fill="#71717A" fontSize="9" fontFamily="monospace">{cameraLabel}</text>
        </svg>
      </div>

      {/* REFLECTED */}
      <div className="bg-[#1a1a1d] border border-white/10 rounded-lg p-4">
        <p className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-3 text-center">{reflected}</p>
        <svg viewBox="0 0 220 150" className="w-full h-32" aria-hidden="true">
          <circle cx="30" cy="30" r="14" fill="#F5A524" />
          {[...Array(8)].map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <line key={i} x1={30 + Math.cos(a) * 18} y1={30 + Math.sin(a) * 18} x2={30 + Math.cos(a) * 24} y2={30 + Math.sin(a) * 24} stroke="#F5A524" strokeWidth="1.5" />;
          })}
          {/* Light hits subject */}
          {[35, 50, 65].map((y, i) => (
            <line key={i} x1="48" y1={y} x2="105" y2="85" stroke="#F5A524" strokeWidth="0.8" strokeDasharray="2,2" opacity="0.6" />
          ))}
          {/* Subject */}
          <circle cx="120" cy="70" r="8" fill="none" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="78" x2="120" y2="105" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="88" x2="108" y2="100" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="88" x2="132" y2="100" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="105" x2="112" y2="125" stroke="#D4D4D8" strokeWidth="2" />
          <line x1="120" y1="105" x2="128" y2="125" stroke="#D4D4D8" strokeWidth="2" />
          {/* Reflected rays toward camera */}
          {[80, 90, 100].map((y, i) => (
            <line key={i} x1="128" y1={y} x2="180" y2={75 + i * 3} stroke="#10B981" strokeWidth="0.8" strokeDasharray="3,2" opacity="0.7" />
          ))}
          {/* Meter at camera position, pointing at subject */}
          <circle cx="195" cy="90" r="6" fill="#F5A524" stroke="#fff" strokeWidth="1.5" />
          <rect x="180" y="68" width="22" height="14" rx="2" fill="#52525B" stroke="#A1A1AA" />
          <circle cx="191" cy="75" r="4" fill="#27272A" stroke="#A1A1AA" />
          <text x="120" y="142" textAnchor="middle" fill="#71717A" fontSize="9" fontFamily="monospace">{subjectLabel}</text>
          <text x="195" y="110" textAnchor="middle" fill="#71717A" fontSize="9" fontFamily="monospace">{meterLabel} + {cameraLabel}</text>
        </svg>
      </div>
    </div>
  );
}

const CONTENT = {
  en: {
    page_title: "The Light Meter: Why Pros Still Carry One",
    page_sub: "Every modern camera has a built-in meter. Yet professionals still carry a handheld light meter into every studio shoot. This lesson covers WHY they do, the critical difference between incident and reflected metering, and how to use a meter in a real shoot — brand-agnostic, applies to any model from a $150 Sekonic L-308X to a $700 L-858D.",
    download: "Download as PDF",
    generating: "Generating...",
    retry: "Retry PDF",
    section1_title: "1. Why your camera's built-in meter sometimes lies",
    section1_body: "Your camera's meter measures light AFTER it has bounced off the subject (reflected metering). That measurement is then averaged and compared to a hard-coded reference: 18% middle gray. The meter assumes EVERY scene should average out to 18% gray. This works for most everyday shots — but it FAILS predictably in five scenarios:",
    fail_cases: [
      "Bride in a white dress against a white wall — meter sees lots of bright light bouncing back, thinks the scene is overexposed, darkens everything → underexposed gray dress.",
      "Black tuxedo against a dark background — meter sees little reflected light, brightens everything → washed-out gray suit, not the rich black you saw.",
      "Snowy landscape — same trap as the bride. Snow comes out gray.",
      "Studio strobes — your camera CANNOT meter through-the-lens flash exposure. The meter only sees continuous light. You're shooting blind.",
      "Mixed ambient + flash — you need to know BOTH exposures separately to balance them. The camera meter can only give you the combined reading."
    ],
    section2_title: "2. Incident vs Reflected — the most important distinction",
    section2_body: "A handheld light meter solves these problems because it can switch between two measurement modes. Understanding which to use is everything.",
    incident_title: "Incident Metering (most accurate, the pro default)",
    incident_what: "Measures the light FALLING ON the subject — before it bounces off them. The white plastic dome (called a 'lumisphere') sits at the subject's position and reads the light flooding in from all angles.",
    incident_use: "Hold the meter AT the subject's face (or have an assistant hold it), with the dome pointed BACK TOWARD the camera. The meter reads what the light source is putting out, NOT how the subject reflects it. Result: a bride in a white dress meters at the SAME exposure as a groom in a black tuxedo in the same light. The meter is never fooled by subject tone.",
    reflected_title: "Reflected Metering",
    reflected_what: "Same principle as your camera's built-in meter — measures light bouncing OFF the subject toward the camera. Useful when you can't physically reach the subject (wildlife, sports, landscapes).",
    reflected_use: "Most handheld meters offer this as a secondary mode. Combined with a 1-5° spot attachment, it becomes a 'spot meter' for the Zone System — letting you place specific tones at chosen exposures. Used by landscape photographers (Ansel Adams style) and cinema teams.",
    section3_title: "3. When you NEED a light meter (vs when the camera is fine)",
    need_yes: [
      "Studio strobe work — no other tool can meter flash. Without it you're test-shot-and-pray.",
      "Mixed ambient + flash — you must read each separately, then choose your blend.",
      "Multi-camera shoots (DSLR + cinema cam + drone) — meter once, set all cameras to the same incident reading for perfectly matched footage.",
      "Brides, snow, black formal wear, white-on-white product shots — anywhere the subject tone fools the in-camera meter.",
      "Film photography — no LCD to chimp. The meter is your only feedback.",
      "Video — you need ONE fixed exposure across an entire clip, no auto. Meter once, lock everything down."
    ],
    need_no: [
      "Walking around in changing daylight — Aperture Priority + spot metering handles it.",
      "Documentary / street / journalism — speed beats precision. The in-camera meter is good enough.",
      "Sports and wildlife — subject is moving, no time to meter manually. Auto modes + experience win.",
      "Travel snapshots — overkill. Skip it."
    ],
    section4_title: "4. Field workflow: AMBIENT light incident reading",
    ambient_steps: [
      "Set the meter's ISO to match your camera (e.g., ISO 200). They MUST match.",
      "Set the meter's shutter speed to what you'll use on the camera (e.g., 1/125 sec).",
      "Switch the meter to AMBIENT mode (usually a sun icon, sometimes labeled 'A' or 'EV').",
      "Stand AT the subject's position. If they're posing already, ask them to hold and lift the meter to their cheek.",
      "Point the white dome BACK at the camera (NOT at the light source).",
      "Press the measure button. The meter displays the required f-stop (e.g., f/5.6 or f/5.6 + 3/10).",
      "Walk back, set your camera to that aperture, and shoot. Skin tones, white shirts, and black suits will all expose correctly."
    ],
    section5_title: "5. Field workflow: FLASH incident reading (the meter's killer feature)",
    flash_steps: [
      "Set ISO to match camera.",
      "Set sync shutter speed (usually 1/125 or 1/200 — check your camera's max flash sync spec).",
      "Switch the meter to FLASH mode (usually a lightning-bolt icon).",
      "Position meter at subject's face, dome aimed at camera.",
      "Press the meter button — the meter enters a 'waiting for flash' state (usually 30-90 seconds).",
      "Trigger your strobe manually (test-fire button or a wireless trigger). The meter samples the flash burst + a tiny bit of ambient.",
      "Meter displays the required aperture for that flash power (e.g., f/8). The meter may also show the ratio of flash-to-ambient if it's a dual-reading model.",
      "Set camera to that aperture. Shoot. If the result is too bright/dark, adjust the FLASH POWER (1/4, 1/8, 1/16) and remeter — never change the camera settings during a strobe session."
    ],
    section6_title: "6. Reading the meter — translating numbers to camera settings",
    reading_body: "The three exposure variables are ISO, shutter speed, and aperture. You set TWO on the meter and it gives you the THIRD. Most photographers set ISO + shutter and let the meter solve for aperture (because aperture controls depth of field — you want to choose it deliberately based on what you read). Examples:",
    reading_examples: [
      "Outdoor portrait at ISO 200, shutter 1/250 → meter says f/4. Set camera: ISO 200, 1/250, f/4. Done.",
      "Studio strobe at ISO 100, sync 1/200 → meter says f/8. Set camera, fire away. Skin tones will be perfect.",
      "Mixed scene at ISO 400, shutter 1/60 → meter shows ambient f/2.8 and flash f/5.6. Means flash is 2 stops brighter than ambient — moody dramatic lighting. Want flatter fill? Drop flash power until they match.",
      "If the meter gives a fractional reading (f/5.6 + 3/10), round to the nearest third stop on your camera (f/5.6, f/6.3, or f/7.1) — modern cameras shoot in 1/3 stop steps."
    ],
    section7_title: "7. Common mistakes",
    mistakes: [
      "Pointing the dome at the LIGHT instead of the camera — measures the source brightness, not what reaches the subject's face from all angles.",
      "Forgetting to match ISO between meter and camera — every wrong ISO step is a full stop off.",
      "Shooting flash at 1/500 with a 1/200 sync limit — you'll get a black bar across the frame. Always check sync speed.",
      "Holding the meter inside your own shadow — you just metered shadow, not the actual light hitting the subject.",
      "Using reflected when incident would solve the problem — handheld reflected is no better than the camera's built-in meter.",
      "Trusting the meter for HDR / extreme contrast scenes without bracketing — meters give one number, HDR needs three."
    ],
    section8_title: "8. Choosing a meter (general guidance)",
    choosing_body: "Almost any meter from the major brands (Sekonic, Kenko, Gossen) made in the last 15 years is reliable. Three tiers:",
    choosing_tiers: [
      "Entry / pocket-size ($150-200): Ambient + flash, incident + reflected, simple LCD. Perfect for one-photographer studios. Example class: Sekonic L-308 series.",
      "Mid / touchscreen ($400-500): Adds memory presets, multi-shot averaging, custom white-balance metering, and a 1-5 degree spot mode. Example class: Sekonic L-478 series.",
      "Pro / cinematographer ($600-800): Adds dedicated spot lens, exposure profile calibration for individual cameras, wireless strobe triggering. Example class: Sekonic L-858 series."
    ],
    pro_tip: "Pro insight: the meter pays for itself the first time you shoot studio strobes for a paying client and nail the exposure without a single chimping cycle. The client thinks you're a wizard. You're not — you just measured.",
    closing: "A light meter doesn't replace your camera's meter — it complements it for the situations where through-the-lens metering fails. Carry it in your studio kit, leave it home for street and wildlife. Once you've used one for a paid strobe session, you'll never go back to test-shot guesswork.",
  },
  es: {
    page_title: "El Fotometro: Por Que los Pros Aun Cargan Uno",
    page_sub: "Cada camara moderna tiene un fotometro integrado. Aun asi, los profesionales siguen llevando un fotometro de mano a cada sesion de estudio. Esta leccion cubre POR QUE lo hacen, la diferencia critica entre medicion incidente y reflejada, y como usar un fotometro en una sesion real — agnostico de marca, aplica a cualquier modelo desde un Sekonic L-308X de $150 hasta un L-858D de $700.",
    download: "Descargar como PDF",
    generating: "Generando...",
    retry: "Reintentar PDF",
    section1_title: "1. Por que el fotometro integrado de tu camara a veces miente",
    section1_body: "El fotometro de tu camara mide la luz DESPUES de que ha rebotado del sujeto (medicion reflejada). Esa medicion se promedia y se compara con una referencia codificada: gris medio del 18%. El fotometro asume que CADA escena deberia promediar al 18% gris. Esto funciona para la mayoria de tomas cotidianas — pero FALLA de forma predecible en cinco escenarios:",
    fail_cases: [
      "Novia con vestido blanco contra pared blanca — el fotometro ve mucha luz brillante rebotando, cree que la escena esta sobreexpuesta, oscurece todo → vestido gris subexpuesto.",
      "Esmoquin negro contra fondo oscuro — el fotometro ve poca luz reflejada, ilumina todo → traje gris descolorido, no el negro rico que viste.",
      "Paisaje nevado — misma trampa que la novia. La nieve sale gris.",
      "Strobes de estudio — tu camara NO PUEDE medir la exposicion de flash a traves del lente. El fotometro solo ve luz continua. Estas disparando a ciegas.",
      "Ambiente mixto + flash — necesitas conocer AMBAS exposiciones por separado para balancearlas. El fotometro de la camara solo da la lectura combinada."
    ],
    section2_title: "2. Incidente vs Reflejada — la distincion mas importante",
    section2_body: "Un fotometro de mano resuelve estos problemas porque puede cambiar entre dos modos de medicion. Entender cual usar lo es todo.",
    incident_title: "Medicion Incidente (la mas precisa, el modo pro por defecto)",
    incident_what: "Mide la luz que CAE SOBRE el sujeto — antes de que rebote de el. La cupula de plastico blanco (llamada 'lumisfera') se posiciona en la posicion del sujeto y lee la luz que llega de todos los angulos.",
    incident_use: "Sostiene el fotometro EN el rostro del sujeto (o que un asistente lo sostenga), con la cupula apuntando DE VUELTA HACIA la camara. El fotometro lee lo que la fuente de luz emite, NO como el sujeto la refleja. Resultado: una novia en vestido blanco mide a la MISMA exposicion que un novio en esmoquin negro bajo la misma luz. El fotometro nunca se deja engañar por el tono del sujeto.",
    reflected_title: "Medicion Reflejada",
    reflected_what: "Mismo principio que el fotometro integrado de tu camara — mide la luz que rebota DEL sujeto hacia la camara. Util cuando no puedes alcanzar fisicamente al sujeto (vida silvestre, deportes, paisajes).",
    reflected_use: "La mayoria de fotometros de mano ofrecen esto como modo secundario. Combinado con un accesorio de punto de 1-5 grados, se convierte en un 'fotometro puntual' para el Sistema Zonal — permitiendote colocar tonos especificos en exposiciones elegidas. Usado por fotografos de paisaje (estilo Ansel Adams) y equipos de cine.",
    section3_title: "3. Cuando NECESITAS un fotometro (vs cuando la camara basta)",
    need_yes: [
      "Trabajo con strobes de estudio — ninguna otra herramienta mide flash. Sin el, disparas y rezas.",
      "Ambiente mixto + flash — debes leer cada uno por separado, luego elegir tu mezcla.",
      "Sesiones multi-camara (DSLR + camara cine + dron) — mide una vez, configura todas las camaras a la misma lectura incidente para metraje perfectamente coincidente.",
      "Novias, nieve, ropa negra formal, productos blanco-sobre-blanco — donde el tono del sujeto engaña al fotometro de la camara.",
      "Fotografia de pelicula — no hay LCD para revisar. El fotometro es tu unica retroalimentacion.",
      "Video — necesitas UNA exposicion fija a lo largo de todo un clip, sin auto. Mide una vez, bloquea todo."
    ],
    need_no: [
      "Caminar con luz diurna cambiante — Prioridad de Apertura + medicion puntual lo maneja.",
      "Documental / calle / periodismo — la velocidad gana a la precision. El fotometro de la camara es suficiente.",
      "Deportes y vida silvestre — el sujeto se mueve, no hay tiempo para medir manualmente. Modos auto + experiencia ganan.",
      "Snapshots de viaje — exagerado. Salta esto."
    ],
    section4_title: "4. Flujo de campo: lectura incidente de luz AMBIENTAL",
    ambient_steps: [
      "Configura el ISO del fotometro para coincidir con tu camara (ej. ISO 200). DEBEN coincidir.",
      "Configura la velocidad de obturacion del fotometro a la que usaras en la camara (ej. 1/125 seg).",
      "Cambia el fotometro a modo AMBIENTE (usualmente icono de sol, a veces etiquetado 'A' o 'EV').",
      "Posicionate EN la posicion del sujeto. Si ya estan posando, pideles que mantengan la pose y levanta el fotometro a su mejilla.",
      "Apunta la cupula blanca DE VUELTA a la camara (NO a la fuente de luz).",
      "Presiona el boton de medir. El fotometro muestra el numero f requerido (ej. f/5.6 o f/5.6 + 3/10).",
      "Regresa, ajusta tu camara a esa apertura, y dispara. Tonos de piel, camisas blancas y trajes negros se expondran correctamente."
    ],
    section5_title: "5. Flujo de campo: lectura incidente de FLASH (la funcion estrella del fotometro)",
    flash_steps: [
      "Configura ISO para coincidir con la camara.",
      "Configura velocidad de sincronizacion (usualmente 1/125 o 1/200 — revisa la especificacion maxima de sincro flash de tu camara).",
      "Cambia el fotometro a modo FLASH (usualmente icono de rayo).",
      "Posiciona el fotometro en el rostro del sujeto, cupula apuntando a la camara.",
      "Presiona el boton del fotometro — el fotometro entra en estado de 'esperando flash' (usualmente 30-90 segundos).",
      "Dispara tu strobe manualmente (boton de prueba o disparador inalambrico). El fotometro muestrea el destello + un poco de ambiente.",
      "El fotometro muestra la apertura requerida para esa potencia de flash (ej. f/8). El fotometro tambien puede mostrar la proporcion flash-a-ambiente si es un modelo de doble lectura.",
      "Ajusta la camara a esa apertura. Dispara. Si el resultado es muy brillante/oscuro, ajusta la POTENCIA DEL FLASH (1/4, 1/8, 1/16) y mide otra vez — nunca cambies los ajustes de camara durante una sesion con strobes."
    ],
    section6_title: "6. Leyendo el fotometro — traduciendo numeros a ajustes de camara",
    reading_body: "Las tres variables de exposicion son ISO, velocidad de obturacion y apertura. Configuras DOS en el fotometro y este te da la TERCERA. La mayoria de fotografos configuran ISO + obturacion y dejan que el fotometro resuelva la apertura (porque la apertura controla profundidad de campo — quieres elegirla deliberadamente segun lo que leas). Ejemplos:",
    reading_examples: [
      "Retrato exterior a ISO 200, obturacion 1/250 → el fotometro dice f/4. Ajusta camara: ISO 200, 1/250, f/4. Listo.",
      "Strobe de estudio a ISO 100, sincro 1/200 → el fotometro dice f/8. Ajusta camara, dispara. Los tonos de piel saldran perfectos.",
      "Escena mixta a ISO 400, obturacion 1/60 → el fotometro muestra ambiente f/2.8 y flash f/5.6. Significa que el flash es 2 pasos mas brillante que el ambiente — iluminacion dramatica con sombras. Quieres fill mas plano? Baja potencia de flash hasta que coincidan.",
      "Si el fotometro da una lectura fraccionaria (f/5.6 + 3/10), redondea al tercio mas cercano en tu camara (f/5.6, f/6.3, o f/7.1) — las camaras modernas disparan en pasos de 1/3."
    ],
    section7_title: "7. Errores comunes",
    mistakes: [
      "Apuntar la cupula a la LUZ en vez de a la camara — mide el brillo de la fuente, no lo que llega al rostro del sujeto desde todos los angulos.",
      "Olvidar igualar el ISO entre fotometro y camara — cada paso de ISO equivocado es un paso completo de exposicion.",
      "Disparar flash a 1/500 con limite de sincro de 1/200 — obtendras una banda negra en el cuadro. Siempre revisa la velocidad de sincro.",
      "Sostener el fotometro dentro de tu propia sombra — acabas de medir sombra, no la luz real que llega al sujeto.",
      "Usar reflejada cuando incidente resolveria el problema — la reflejada de mano no es mejor que el fotometro integrado de la camara.",
      "Confiar en el fotometro para escenas HDR / contraste extremo sin bracketing — los fotometros dan un numero, HDR necesita tres."
    ],
    section8_title: "8. Eligiendo un fotometro (guia general)",
    choosing_body: "Casi cualquier fotometro de las marcas mayores (Sekonic, Kenko, Gossen) hecho en los ultimos 15 años es confiable. Tres niveles:",
    choosing_tiers: [
      "Entrada / tamaño bolsillo ($150-200): Ambiente + flash, incidente + reflejado, LCD simple. Perfecto para estudios de un fotografo. Clase ejemplo: serie Sekonic L-308.",
      "Medio / pantalla tactil ($400-500): Añade memoria de presets, promedio multi-toma, medicion custom de balance de blancos, y modo puntual de 1-5 grados. Clase ejemplo: serie Sekonic L-478.",
      "Pro / cinematografo ($600-800): Añade lente puntual dedicado, calibracion de perfil de exposicion para camaras individuales, disparo inalambrico de strobes. Clase ejemplo: serie Sekonic L-858."
    ],
    pro_tip: "Insight pro: el fotometro se paga solo la primera vez que disparas strobes de estudio para un cliente pagando y clavas la exposicion sin un solo ciclo de revisar la LCD. El cliente cree que eres un mago. No lo eres — solo mediste.",
    closing: "Un fotometro no reemplaza el fotometro de tu camara — lo complementa para las situaciones donde la medicion a traves del lente falla. Cargalo en tu kit de estudio, dejalo en casa para calle y vida silvestre. Una vez que lo uses para una sesion pagada con strobes, no volveras a la adivinanza por test-shot.",
  },
};

// ---------------------------------------------------------------------------
// Direct jsPDF builder — fast, reliable, text-selectable.
// ---------------------------------------------------------------------------
function buildLightMeterPDF(c) {
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const margin = 15;
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - margin * 2;
  let y = margin;

  const ensure = (n) => {
    if (y + n > pageH - margin) {
      pdf.addPage();
      y = margin;
    }
  };

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(20);
  pdf.text(c.page_title, pageW / 2, y + 4, { align: "center" });
  y += 10;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(110);
  const subL = pdf.splitTextToSize(c.page_sub, contentW);
  pdf.text(subL, pageW / 2, y, { align: "center" });
  y += subL.length * 4 + 6;
  pdf.setTextColor(0);

  const heading = (t) => {
    ensure(12);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text(t, margin, y);
    y += 1.5;
    pdf.setDrawColor(180);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageW - margin, y);
    y += 5;
  };
  const para = (t) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(60);
    const ls = pdf.splitTextToSize(t, contentW);
    ensure(ls.length * 3.8 + 1);
    pdf.text(ls, margin, y);
    y += ls.length * 3.8 + 3;
  };
  const list = (items, ordered = false) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(60);
    items.forEach((it, i) => {
      const prefix = ordered ? `${i + 1}.  ` : "•  ";
      const ls = pdf.splitTextToSize(prefix + it, contentW - 2);
      ensure(ls.length * 3.6 + 1);
      pdf.text(ls, margin + 2, y);
      y += ls.length * 3.6 + 1;
    });
    y += 2;
  };
  const subHeading = (t) => {
    ensure(6);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10.5);
    pdf.setTextColor(20);
    pdf.text(t, margin, y);
    y += 4.5;
  };

  heading(c.section1_title);
  para(c.section1_body);
  list(c.fail_cases);

  heading(c.section2_title);
  para(c.section2_body);
  subHeading(c.incident_title);
  para(c.incident_what);
  para(c.incident_use);
  subHeading(c.reflected_title);
  para(c.reflected_what);
  para(c.reflected_use);

  heading(c.section3_title);
  subHeading("YES — use a light meter:");
  list(c.need_yes);
  subHeading("NO — camera meter is fine:");
  list(c.need_no);

  heading(c.section4_title);
  list(c.ambient_steps, true);

  heading(c.section5_title);
  list(c.flash_steps, true);

  heading(c.section6_title);
  para(c.reading_body);
  list(c.reading_examples);

  heading(c.section7_title);
  list(c.mistakes);

  heading(c.section8_title);
  para(c.choosing_body);
  list(c.choosing_tiers);

  ensure(15);
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(9);
  pdf.setTextColor(80);
  const proL = pdf.splitTextToSize(c.pro_tip, contentW);
  pdf.text(proL, margin, y);
  y += proL.length * 3.6 + 4;
  const clL = pdf.splitTextToSize(c.closing, contentW);
  ensure(clL.length * 3.6 + 1);
  pdf.text(clL, margin, y);

  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.setTextColor(150);
    pdf.text(`www.dslradvisor.com   ${i} / ${total}`, pageW / 2, pageH - 6, { align: "center" });
  }
  return pdf.output("blob");
}

export default function LightMeterPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(false);

  const download = async () => {
    setError(false);
    setGenerating(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => r()));
      const blob = buildLightMeterPDF(c);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `light-meter-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[LightMeter PDF] failed:", err);
      setError(true);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="light-meter-page">
      <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
        <motion.div variants={itemAnim} className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">{c.page_title}</h1>
            <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
          </div>
          <Button
            onClick={download}
            disabled={generating}
            className={`${error ? "bg-red-500 hover:bg-red-600 text-white" : "bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A]"} font-medium disabled:opacity-60`}
            data-testid="light-meter-download-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" />
            {generating ? c.generating : (error ? c.retry : c.download)}
          </Button>
        </motion.div>

        {/* 1. Why built-in fails */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Camera weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section1_title}</h2>
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.section1_body}</p>
          <ul className="space-y-2">
            {c.fail_cases.map((f, i) => (
              <li key={i} className="text-[#A1A1AA] text-sm flex gap-2 leading-relaxed">
                <span className="text-rose-400 shrink-0">✗</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* 2. Incident vs Reflected */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section2_title}</h2>
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.section2_body}</p>

          <IncidentVsReflectedDiagram lang={lang} />

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-emerald-400 text-sm font-semibold mb-2">{c.incident_title}</p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-2"><strong className="text-[#D4D4D8]">What:</strong> {c.incident_what}</p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed"><strong className="text-[#D4D4D8]">How:</strong> {c.incident_use}</p>
            </div>
            <div className="bg-sky-500/5 border border-sky-500/20 rounded-lg p-4">
              <p className="text-sky-400 text-sm font-semibold mb-2">{c.reflected_title}</p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mb-2"><strong className="text-[#D4D4D8]">What:</strong> {c.reflected_what}</p>
              <p className="text-[#A1A1AA] text-sm leading-relaxed"><strong className="text-[#D4D4D8]">How:</strong> {c.reflected_use}</p>
            </div>
          </div>
        </motion.div>

        {/* 3. When do you need it */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <ListChecks weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section3_title}</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-emerald-400 text-xs uppercase tracking-widest font-semibold mb-2">{lang === "es" ? "Si — usa fotometro" : "YES — use a light meter"}</p>
              <ul className="space-y-1.5">
                {c.need_yes.map((n, i) => (
                  <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                    <span className="text-emerald-400 shrink-0">✓</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-4">
              <p className="text-rose-400 text-xs uppercase tracking-widest font-semibold mb-2">{lang === "es" ? "No — la camara basta" : "NO — camera meter is fine"}</p>
              <ul className="space-y-1.5">
                {c.need_no.map((n, i) => (
                  <li key={i} className="text-[#A1A1AA] text-sm flex gap-2">
                    <span className="text-rose-400 shrink-0">✗</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* 4. Ambient workflow */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sun weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section4_title}</h2>
          </div>
          <ol className="space-y-2">
            {c.ambient_steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* 5. Flash workflow */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightning weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section5_title}</h2>
          </div>
          <ol className="space-y-2">
            {c.flash_steps.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </motion.div>

        {/* 6. Reading the meter */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Gauge weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section6_title}</h2>
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.reading_body}</p>
          <ul className="space-y-2">
            {c.reading_examples.map((e, i) => (
              <li key={i} className="text-[#A1A1AA] text-sm flex gap-2 leading-relaxed border-l-2 border-[#F5A524]/30 pl-3">
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* 7. Mistakes */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Camera weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section7_title}</h2>
          </div>
          <ul className="space-y-2">
            {c.mistakes.map((m, i) => (
              <li key={i} className="text-[#A1A1AA] text-sm flex gap-2 leading-relaxed">
                <span className="text-rose-400 shrink-0">✗</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* 8. Choosing */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb weight="fill" className="w-5 h-5 text-[#F5A524]" />
            <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.section8_title}</h2>
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.choosing_body}</p>
          <ul className="space-y-2">
            {c.choosing_tiers.map((t, i) => (
              <li key={i} className="text-[#A1A1AA] text-sm flex gap-2 leading-relaxed border-l-2 border-[#F5A524]/30 pl-3">
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Pro tip + closing */}
        <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6 space-y-3">
          <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.pro_tip}</p>
          <p className="text-[#D4D4D8] text-sm leading-relaxed">{c.closing}</p>
        </motion.div>
      </motion.div>
    </div>
  );
}
